import { useMemo, useState } from "react";
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { cn } from "../../../lib/utils.js";
import { ChartTooltipCard } from "./ChartTooltip.js";
import { pct, useChartTheme, useEntryAnimation } from "./chart-theme.js";
import type { CategoryDatum } from "./CategoryDonut.js";

export interface CategoryBarsProps {
  data: CategoryDatum[];
  /** The whole the shares are measured against; defaults to the rows' sum. */
  total?: number;
  /** Rows beyond this are dropped (sorted by value first). */
  maxBars?: number;
  selectedId?: string | null;
  onSelect?: ((id: string | null) => void) | undefined;
  formatValue?: (n: number) => string;
  /** Width reserved for the category names, in px. */
  labelWidth?: number;
  /** Height of one row, in px. */
  rowHeight?: number;
  emptyMessage?: string;
  className?: string;
}

/**
 * Magnitude by category: horizontal Recharts bars, one hue.
 *
 * Ranked bars are a magnitude comparison, not an identity set, so they take a
 * single hue — colouring each bar differently would encode rank as identity.
 * Every bar carries its value as a direct label, which is also the relief the
 * palette's contrast warning requires.
 */
export function CategoryBars({
  data, total, maxBars = 6, selectedId, onSelect,
  formatValue = n => String(n), labelWidth = 92, rowHeight = 26,
  emptyMessage = "No data", className,
}: CategoryBarsProps) {
  const theme = useChartTheme();
  const entry = useEntryAnimation();
  const [hovered, setHovered] = useState<string | null>(null);

  const rows = useMemo(
    () => [...data].filter(d => d.value > 0).sort((a, b) => b.value - a.value).slice(0, maxBars)
      .map(d => ({ key: d.id ?? d.name, id: d.id, name: d.name, value: d.value })),
    [data, maxBars],
  );
  const whole = total ?? rows.reduce((s, d) => s + d.value, 0);

  if (rows.length === 0) {
    return <div className={cn("py-6 text-center text-[13px] text-surface-400", className)}>{emptyMessage}</div>;
  }

  return (
    <div className={cn("w-full", className)} style={{ height: rows.length * rowHeight + 8 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={rows}
          margin={{ top: 0, right: 34, bottom: 0, left: 0 }}
          barCategoryGap={6}
          onMouseLeave={() => setHovered(null)}
        >
          <XAxis type="number" hide domain={[0, "dataMax"]} />
          <YAxis
            type="category"
            dataKey="name"
            width={labelWidth}
            tickLine={false}
            axisLine={false}
            tick={{ fill: theme.textMuted, fontSize: 11 }}
          />
          <Tooltip
            isAnimationActive={false}
            cursor={{ fill: theme.track, opacity: 0.6 }}
            wrapperStyle={{ outline: "none" }}
            content={({ active, payload }) => {
              const row = active && payload?.length ? payload[0]?.payload as (typeof rows)[number] | undefined : undefined;
              if (!row) return null;
              return (
                <ChartTooltipCard rows={[{
                  name: row.name,
                  value: `${formatValue(row.value)} · ${pct(row.value, whole)}`,
                  swatchClass: "bg-[var(--chart-1)]",
                }]} />
              );
            }}
          />
          <Bar
            dataKey="value"
            // 4px rounded data-end, anchored flat to the baseline.
            radius={[0, 4, 4, 0]}
            barSize={12}
            // Each bar grows out of the axis on first paint.
            isAnimationActive={entry.active}
            animationBegin={0}
            animationDuration={entry.duration}
            animationEasing="ease-out"
            onAnimationEnd={entry.onEnd}
          >
            {rows.map(r => (
              <Cell
                key={r.key}
                fill={theme.series[0]!}
                opacity={(selectedId != null && selectedId !== r.key) || (hovered != null && hovered !== r.key) ? 0.4 : 1}
                className={onSelect ? "cursor-pointer outline-none" : "outline-none"}
                onMouseEnter={() => setHovered(r.key)}
                {...(onSelect ? { onClick: () => onSelect(selectedId === r.key ? null : r.id) } : {})}
              />
            ))}
            <LabelList
              dataKey="value"
              position="right"
              offset={8}
              fill={theme.text}
              fontSize={11}
              fontWeight={600}
              formatter={v => formatValue(Number(v ?? 0))}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
